import React, { memo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useBedAssignment } from "../../hooks/useBedAssignment";

import { ADMISSION_COLORS as C } from "../../constants/admissionTheme";

function BedGrid({ patient, setPatient, errors = {}, disabled = false, hasError = false }) {
  const [expanded, setExpanded] = useState(false);
  const showHighlight = hasError && !expanded;
  const selectedWard = patient.encounter.wardNo || "";
  const selectedBed = patient.encounter.bedId || "";
  const selectedRoom = patient.encounter.roomNo || "";

  const {
    bedTypeList,
    bedList,
    loadingBedTypes,
    loadingBeds,
    getSelectedWardDetails,
  } = useBedAssignment(selectedWard);

  const selectedWardDetails = getSelectedWardDetails(selectedWard);

  const handleSelectBedType = (ward) => {
    if (disabled) return;
    setPatient((s) => ({
      ...s,
      encounter: {
        ...s.encounter,
        wardNo: ward.bedTypeCode,
        bedTypeName: ward.bedTypeName,
        roomNo: "",
        bedId: "",
      },
    }));
  };

  const handleSelectBed = (bed) => {
    if (disabled) return;
    if (!bed.isAvailable) return;
    setPatient((s) => ({
      ...s,
      encounter: {
        ...s.encounter,
        bedId: bed.bedNo,
        roomNo: bed.roomNo || "",
      },
    }));
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        borderRadius: "12px !important",
        border: `1px solid ${showHighlight ? C.error.border : C.border}`,
        boxShadow: showHighlight ? `0 0 0 1px ${C.error.border}` : C.shadow.card,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: showHighlight ? C.error.text : undefined }} />}
        sx={{
          bgcolor: showHighlight ? C.error.bg : C.panelHeaderBg,
          borderRadius: "12px",
          minHeight: 56,
          "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.5 },
        }}
      >
        <Typography fontWeight={700} fontSize="1.125rem" color={showHighlight ? C.error.text : C.textPrimary}>
          Bed Type, Bed &amp; Room Assignment
          {showHighlight && " — required fields missing"}
        </Typography>
        {selectedWard && selectedBed && (
          <Chip
            label={`${selectedWardDetails?.bedTypeName || selectedWard} / Bed ${selectedBed}${selectedRoom ? ` / Room ${selectedRoom}` : ""}`}
            size="small"
            sx={{
              bgcolor: C.chipInfoBg,
              color: C.chipInfoText,
              fontWeight: 600,
              fontSize: "0.75rem",
              height: 24,
              ml: "auto",
            }}
          />
        )}
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2.5, ...(disabled ? { opacity: 0.72, pointerEvents: "none" } : {}) }}>
        {disabled && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Bed assignment cannot be changed for an existing admission.
          </Typography>
        )}
        {errors.bedId && (
          <Typography variant="caption" color="error" sx={{ display: "block", mb: 1.5 }}>
            {errors.bedId}
          </Typography>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={600} color={C.textPrimary} mb={1}>
              Select Bed Type
            </Typography>
            {loadingBedTypes ? (
              <Box sx={{ p: 3, textAlign: "center", color: C.textSecondary, border: `1px dashed ${C.border}`, borderRadius: 2 }}>
                <CircularProgress size={20} sx={{ mb: 1 }} />
                <Typography variant="body2" display="block">Loading bed types…</Typography>
              </Box>
            ) : bedTypeList.length > 0 ? (
              bedTypeList.map((ward) => {
                const isWardSelected = selectedWard === ward.bedTypeCode;
                return (
                  <Card
                    key={`${ward.bedTypeCode}-${ward.bedTypeName}`}
                    onClick={disabled ? undefined : () => handleSelectBedType(ward)}
                    sx={{
                      mb: 1.5,
                      cursor: disabled ? "default" : "pointer",
                      borderRadius: 2,
                      border: isWardSelected ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                      bgcolor: isWardSelected ? C.selectedBg : C.cardBg,
                      boxShadow: isWardSelected ? C.shadow.selected : C.shadow.card,
                      transition: "all 0.2s ease",
                      "&:hover": disabled ? {} : {
                        boxShadow: "0 3px 10px rgba(0,0,0,0.12)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <CardContent sx={{ py: "12px !important", px: 2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color={isWardSelected ? C.primary : C.textPrimary}
                      >
                        {ward.bedTypeName}
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                        <Typography variant="caption" color={C.textMuted}>
                          Total Beds: {ward.totalBedCount}
                        </Typography>
                        <Chip
                          label={`${ward.availableBedCount} Available`}
                          size="small"
                          sx={{
                            bgcolor: ward.availableBedCount > 3
                              ? C.success.bg
                              : ward.availableBedCount > 0
                                ? C.warning.bg
                                : C.error.bg,
                            color: ward.availableBedCount > 3
                              ? C.success.text
                              : ward.availableBedCount > 0
                                ? C.warning.text
                                : C.error.text,
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            height: 20,
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Box sx={{ p: 3, textAlign: "center", color: C.textMuted, border: `1px dashed ${C.border}`, borderRadius: 2 }}>
                <Typography variant="body2">
                  No bed types configured.
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={600} color={C.textPrimary} mb={1}>
              Select Bed
            </Typography>
            {selectedWard ? (
              <Box>
                <Box display="flex" gap={2} mb={1.5} flexWrap="wrap">
                  <LegendDot color={C.success.bg} border={C.success.border} label="Available" />
                  <LegendDot color={C.error.bg} border={C.error.border} label="Occupied" />
                  <LegendDot color={C.primary} border={C.primary} label="Selected" />
                </Box>

                {loadingBeds ? (
                  <Box sx={{ p: 3, textAlign: "center", color: C.textSecondary, border: `1px dashed ${C.border}`, borderRadius: 2 }}>
                    <CircularProgress size={20} sx={{ mb: 1 }} />
                    <Typography variant="body2" display="block">Loading beds…</Typography>
                  </Box>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={1.5}>
                    {bedList.map((bed) => {
                      const isAvailable = bed.isAvailable;
                      const isSelected = selectedBed === bed.bedNo;
                      return (
                        <Box
                          key={bed.bedNo}
                          component="button"
                          type="button"
                          onClick={disabled ? undefined : () => handleSelectBed(bed)}
                          disabled={disabled || !isAvailable}
                          aria-label={`Bed ${bed.bedNo} ${isAvailable ? "available" : "occupied"}`}
                          aria-pressed={isSelected}
                          sx={{
                            width: 80,
                            height: 70,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 2,
                            cursor: isAvailable ? "pointer" : "not-allowed",
                            border: isSelected
                              ? `2px solid ${C.primary}`
                              : isAvailable
                                ? `1px solid ${C.success.border}`
                                : `1px solid ${C.error.border}`,
                            bgcolor: isSelected ? C.primary : isAvailable ? C.success.bg : C.error.bg,
                            transition: "all 0.2s ease",
                            "&:hover": isAvailable
                              ? { transform: "scale(1.05)", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }
                              : {},
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              color: isSelected ? "#fff" : isAvailable ? C.success.text : C.error.text,
                              fontSize: "0.85rem",
                            }}
                          >
                            {bed.bedNo}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isSelected ? "rgba(255,255,255,0.85)" : C.textMuted,
                              fontSize: "0.65rem",
                            }}
                          >
                            {isSelected ? "Selected" : isAvailable ? "Available" : "Occupied"}
                          </Typography>
                        </Box>
                      );
                    })}
                    {bedList.length === 0 && (
                      <Box sx={{ p: 3, textAlign: "center", color: "#aaa", border: "1px dashed #ddd", borderRadius: 2, width: "100%" }}>
                        <Typography variant="body2">No beds found for selected bed type</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: "center", color: C.textMuted, border: `1px dashed ${C.border}`, borderRadius: 2 }}>
                <Typography variant="body2">Select a bed type first</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

function LegendDot({ color, border, label }) {
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: color, border: `1px solid ${border}` }} />
      <Typography variant="caption" color={C.textSecondary}>{label}</Typography>
    </Box>
  );
}

export default memo(BedGrid);
